import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useCreator } from "../creator/CreatorContext";
import { listAssets } from "../data/client";
import type { PatronAsset } from "../data/types";
import { Chip, PipFilter, PipMeter, SearchInput, Sprite } from "../design/primitives";
import { cardSprite } from "../design/sprites";
import { useLocalStorage } from "../utils";
import {
  DEFAULT_FILTERS,
  matchesDifficulty,
  readFilters,
  recallScroll,
  rememberCollection,
  rememberScroll,
  writeFilters,
  type Direction,
  type SortKey,
  type ViewMode,
} from "../library/gridState";
import { AssetCard, formatAdded } from "./AssetCard";
import { ErrorState, LoadingState, NoResultsState } from "./states";
import css from "./library.module.css";

type Load =
  | { phase: "loading" }
  | { phase: "error"; error: Error }
  | { phase: "ready"; assets: PatronAsset[] };

export default function Library() {
  const { creator, gameById } = useCreator();
  const [load, setLoad] = useState<Load>({ phase: "loading" });

  /*
   * Filters live in the URL, not in this component - R-D1.
   *
   * They used to be five `useState` calls, which died on unmount. That was fine
   * while the grid was the only screen that cared; it stopped being fine when
   * opening a tab and pressing `< TABS` had to land the reader back on the card
   * they left with the filter still on (R-6).
   *
   * `replace`, not `push`, on every change: typing five characters into the
   * search box must not put five entries in the history, or browser back
   * becomes a backspace key.
   */
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const filters = readFilters(params);
  const { query, diffMode, diffValue, franchise, game, sortKey, direction } = filters;
  const update = (next: Partial<typeof filters>) =>
    // `params` is passed so a filter change keeps what it does not own — most
    // visibly `?as=`, whose loss remounted the whole library on every keystroke.
    setParams(writeFilters({ ...filters, ...next }, params), { replace: true });

  // §24: view mode persists. A standing preference rather than a description of
  // one query, so it stays in localStorage rather than joining the URL.
  const [view, setView] = useLocalStorage<ViewMode>("perks.view", "grid");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let live = true;
    setLoad({ phase: "loading" });
    listAssets()
      .then((assets) => live && setLoad({ phase: "ready", assets }))
      .catch((error: Error) => live && setLoad({ phase: "error", error }));
    return () => {
      live = false;
    };
  }, [reloadKey]);

  /*
   * R-6: put the reader back where they were.
   *
   * After the rows exist, not on mount. On mount the page is a loading state a
   * few hundred pixels tall, so `scrollTo(1400)` clamps to the bottom of that
   * and the restore silently becomes "top". Keyed on the load phase for exactly
   * that reason.
   *
   * Keyed on the phase alone, with no "have I already done this" flag. One was
   * written and removed: the effect only re-runs when `load.phase` changes, a
   * keystroke does not change it, and the one path that reaches `ready` twice
   * goes through the error state - where nothing was restored to guard against.
   * Removing the flag changed no test, which is how it was found (W-6).
   */
  useEffect(() => {
    if (load.phase !== "ready") return;
    const offset = recallScroll();
    if (offset > 0) window.scrollTo(0, offset);
  }, [load.phase]);

  /*
   * Remember it on the way out, not on every scroll event. A scroll listener
   * here would write to sessionStorage on every frame of a flick gesture, and
   * the only moment the value matters is the moment the screen goes away.
   */
  useEffect(() => () => rememberScroll(window.scrollY), []);

  const all = load.phase === "ready" ? load.assets : [];

  /*
   * FROM offers what is on the shelf, not everything the creator has ever
   * typed into Studio. Reported by Adam 2026-09-27.
   *
   * It used to read `franchises` off the creator record, which is built from
   * `creator.games` - every game, including ones whose only tabs are drafts.
   * So the row offered filters that could only ever return NO RESULTS.
   *
   * Derived from `all` instead. `/assets` returns published assets this
   * reader's tier can see, so a chip is present exactly when clicking it finds
   * something; a franchise whose only tab is priced above them disappears by
   * the same rule, which is the same promise kept.
   *
   * `all`, never `filtered` - picking one chip must not remove the others.
   *
   * Retired games stay out even when they have a published tab: retirement is
   * the creator saying "stop offering this" (D-12), and the asset is still
   * reachable under ALL.
   *
   * A blank franchise is not a franchise. Studio creates a game with
   * `franchise: ""` and never makes anyone fill it in, so every game he had
   * not got round to naming collapsed into one empty chip - a button with no
   * label, which reads as a rendering fault rather than as missing data.
   * Reported by Adam 2026-09-27, on prod, the same day the row started being
   * derived at all. Those tabs are still reachable: ALL holds them, and so
   * does search.
   */
  const franchises = useMemo(() => {
    const shelf = new Set<string>();
    for (const asset of all) {
      const assetGame = gameById(asset.gameId);
      if (!assetGame || assetGame.retired) continue;
      const name = assetGame.franchise.trim();
      if (name) shelf.add(name);
    }
    return [...shelf].sort();
  }, [all, gameById]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return all.filter((asset) => {
      const assetGame = gameById(asset.gameId);
      // §7: search covers arrangement titles and game names.
      if (q && !`${asset.title} ${assetGame?.name ?? ""}`.toLowerCase().includes(q)) {
        return false;
      }
      if (!matchesDifficulty(Number(asset.fields.difficulty ?? 0), diffMode, diffValue)) {
        return false;
      }
      if (franchise && assetGame?.franchise !== franchise) return false;
      // R-8: the breadcrumb's game link lands here. Keyed on the game id rather
      // than a slug from its name, so renaming a game in Studio does not
      // silently break every link that was ever shared (R-D4).
      if (game && asset.gameId !== game) return false;
      return true;
    });
  }, [all, query, diffMode, diffValue, franchise, game, gameById]);

  const sorted = useMemo(() => {
    const factor = direction === "asc" ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case "title":
          return factor * a.title.localeCompare(b.title);
        case "game":
          return (
            factor *
            (gameById(a.gameId)?.name ?? "").localeCompare(gameById(b.gameId)?.name ?? "")
          );
        case "difficulty":
          return factor * (Number(a.fields.difficulty ?? 0) - Number(b.fields.difficulty ?? 0));
        case "added":
          return factor * (a.addedAt ?? "").localeCompare(b.addedAt ?? "");
      }
    });
  }, [filtered, sortKey, direction, gameById]);

  /**
   * Opening a tab — R-9, R-D2.
   *
   * Two things travel with the navigation, and they are different in kind:
   *
   * - the **ordered slugs on screen**, so the stepper walks the list the reader
   *   was actually browsing rather than the unfiltered library. `sorted`, not
   *   `all`: a reader who filtered to seven Zelda tabs and opened the second
   *   must see `2 / 7`, and `4 / 27` would be a lie about where they are.
   * - a **flag in navigation state** saying this came from the grid. The stored
   *   list alone is not enough to trust: someone who browsed an hour ago and
   *   then opened a shared link in the same tab still has stored data, and
   *   their stepper would confidently walk somebody else's filters (R-D2).
   *
   * Scroll is not written here — the unmount effect above does it, and it runs
   * on this navigation like any other way of leaving.
   */
  const openAsset = (slug: string) => {
    rememberCollection(sorted.map((a) => a.slug));
    navigate(`/tabs/${slug}`, { state: { fromGrid: true } });
  };

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      update({ direction: direction === "asc" ? "desc" : "asc" });
      return;
    }
    // Dates read newest-first; everything else reads A-Z.
    update({ sortKey: key, direction: key === "added" ? "desc" : "asc" });
  };

  /* Clears what the reader set. Sort is not a filter and stays where it is. */
  const clearFilters = () =>
    update({
      query: DEFAULT_FILTERS.query,
      // The level goes; the direction stays, the way sort does. It is how this
      // reader reads, not part of the question they just asked.
      diffValue: DEFAULT_FILTERS.diffValue,
      franchise: DEFAULT_FILTERS.franchise,
      game: DEFAULT_FILTERS.game,
    });

  if (load.phase === "loading") return <LoadingState />;
  if (load.phase === "error") {
    return (
      <ErrorState
        code={`LIBRARY_LOAD_FAILED · ${load.error.name.toUpperCase()}`}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  const noun = creator.assetTypes.find((t) => t.type === "tab")?.label.toUpperCase() ?? "TABS";
  /*
   * `undefined` when the id names no game the creator has - a link from before
   * a game was deleted, say. The chip then does not render, and the reader's
   * way out is NO RESULTS' own CLEAR FILTERS, which clears this too.
   */
  const activeGame = game ? gameById(game) : undefined;

  return (
    <div className={css.page}>

      <div className={css.searchRow}>
        <SearchInput value={query} onChange={(e) => update({ query: e.target.value })} />
      </div>

      <div className={css.filters}>
        <span className={css.filterLabel}>DIFFICULTY</span>
        {/*
          The comparator — DF-D1. These three buttons are here rather than
          inside `PipFilter` because there is no segmented-control primitive:
          the GRID/LIST toggle below is inline markup using these same three
          classes, and reusing them verbatim is the only way the two cannot
          drift apart. The cost is that one control spans two files.

          Symbols, not words, because the label beside the pips already says
          `UP TO 3` in full — the glyph never has to stand alone.
        */}
        <div className={css.toggle}>
          {(
            [
              ["lte", "≤", "at most"],
              ["eq", "=", "exactly"],
              ["gte", "≥", "at least"],
            ] as const
          ).map(([mode, glyph, label]) => (
            <button
              key={mode}
              type="button"
              aria-pressed={diffMode === mode}
              // The glyph is decoration to a screen reader; the word is the
              // control. NFR-4: the mode must be announced, not inferred from
              // which pips are lit.
              aria-label={`difficulty ${label}`}
              className={`${css.toggleButton} ${diffMode === mode ? css.toggleActive : ""}`}
              onClick={() => update({ diffMode: mode })}
            >
              <span aria-hidden>{glyph}</span>
            </button>
          ))}
        </div>
        <PipFilter
          value={diffValue}
          mode={diffMode}
          onChange={(v) => update({ diffValue: v })}
        />

        <span className={css.filterLabel}>FROM</span>
        {/*
          FROM is **one filter with two grains** — a franchise, or a single game.
          Reported 2026-08-31: the breadcrumb's game link set `?game=` and
          nothing in this row showed it, so arriving that way left the reader
          filtered with no visible control and no route back to everything.

          So the two are mutually exclusive and share the row. Combining them
          would let `game=oot` sit alongside `from=Chrono` and match nothing,
          which is a filter state the reader cannot reason about from what is
          on screen.
        */}
        <div className={css.chips}>
          <Chip
            active={!franchise && !game}
            onClick={() => update({ franchise: null, game: null })}
          >
            ALL
          </Chip>

          {/*
            Only while it is set. Games are creator data and there can be
            dozens, so this is not a standing option in the row - it is the
            filter the reader arrived with, shown so they can take it off.
          */}
          {activeGame && (
            <Chip active onClick={() => update({ game: null })}>
              {activeGame.name.toUpperCase()}
            </Chip>
          )}

          {/* Franchises come from the creator's Games list (§12), never a literal. */}
          {franchises.map((f) => (
            <Chip
              key={f}
              active={franchise === f}
              // Picking a franchise drops the game, and vice versa: one answer
              // to "where is this from", at whichever grain the reader chose.
              onClick={() =>
                update({ franchise: franchise === f ? null : f, game: null })
              }
            >
              {f.toUpperCase()}
            </Chip>
          ))}
        </div>

        <div className={css.filterRight}>
          <span className={css.count}>
            {sorted.length} OF {all.length} {noun}
          </span>
          <div className={css.toggle}>
            {(["grid", "list"] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                aria-pressed={view === mode}
                className={`${css.toggleButton} ${view === mode ? css.toggleActive : ""}`}
                onClick={() => setView(mode)}
              >
                {mode.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/*
        RECENTLY ADDED was here, and is gone - Adam, 2026-09-27: "i dont see
        the point".

        It was also mis-laid-out, and the two are the same fact. The compact
        card is `grid-template-columns: auto minmax(0, 1fr)`, which is right
        for a 64px sprite and wrong for a cover: a cover takes its natural
        width in that `auto` track, so a three-across strip gave the art ~195px
        and left the title wrapping one word per line.

        Deleted rather than commented out, but the pieces it used are kept -
        `AssetCard`'s `compact` variant, `.compact` in assetCard.module.css,
        `.recent` and its 560px scroller in library.module.css. Bringing the
        strip back is this block again; fixing the layout first is capping the
        art column, not rebuilding the card.
      */}

      {sorted.length === 0 ? (
        <NoResultsState query={query || "those filters"} onClear={clearFilters} />
      ) : view === "grid" ? (
        <div className={css.grid}>
          {sorted.map((asset) => (
            <AssetCard key={asset.id} asset={asset} onOpen={openAsset} />
          ))}
        </div>
      ) : (
        <div role="table">
          <div className={css.rowHead} role="row">
            <span />
            <SortHeader label="TITLE" k="title" active={sortKey} dir={direction} onClick={toggleSort} />
            <SortHeader label="GAME" k="game" active={sortKey} dir={direction} onClick={toggleSort} />
            <SortHeader label="DIFFICULTY" k="difficulty" active={sortKey} dir={direction} onClick={toggleSort} />
            <SortHeader label="ADDED" k="added" active={sortKey} dir={direction} onClick={toggleSort} />
            <span />
          </div>
          {sorted.map((asset) => {
            const game = gameById(asset.gameId);
            const typeDef = creator.assetTypes.find((t) => t.type === asset.assetType);
            return (
              <Link
                key={asset.id}
                to={`/tabs/${asset.slug}`}
                className={css.row}
                role="row"
                /*
                 * Still a real `Link` - middle-click and copy-link must keep
                 * working - with the plain left click routed through `openAsset`
                 * so a list row hands over the same collection a card does.
                 */
                onClick={(e) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  openAsset(asset.slug);
                }}
              >
                <Sprite
                  src={cardSprite(typeDef?.cardObject ?? "SHEET", game?.tint ?? "#8A7FB0")}
                  size={44}
                />
                <span className={css.rowTitle}>{asset.title}</span>
                <span className={css.rowGame}>{game?.name ?? "Unknown"}</span>
                <PipMeter value={Number(asset.fields.difficulty ?? 0)} size={10} />
                <span className={css.rowMeta}>{formatAdded(asset.addedAt)}</span>
                <span className={css.rowMeta}>
                  {(typeDef?.slots ?? [])
                    .filter((s) => s.shortName && asset.files[s.id])
                    .map((s) => s.shortName)
                    .join(" · ")}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** §7: "sortable headers as mono buttons with ▲/▼/· indicator". */
function SortHeader({
  label,
  k,
  active,
  dir,
  onClick,
}: {
  label: string;
  k: SortKey;
  active: SortKey;
  dir: Direction;
  onClick: (k: SortKey) => void;
}) {
  const isActive = active === k;
  return (
    <button
      type="button"
      className={`${css.sortButton} ${isActive ? css.sortButtonActive : ""}`}
      onClick={() => onClick(k)}
      aria-sort={isActive ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      {label}
      <span aria-hidden>{isActive ? (dir === "asc" ? "▲" : "▼") : "·"}</span>
    </button>
  );
}
